'use client';
import { TransitionStartFunction, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import cx from 'classnames';
import { navigateTo } from '@cat-ecom/common/utils/browserUtils';
import { getDesktopImageWidthHeight } from '../../utils/utils';
import { getFamilyName } from 'services/equipment/utils';
import { getProductName } from '@cat-ecom/common/utils/stringUtils';
import {
  Conditional,
  CustomerAccountEquipmentActionWrapper,
  useBreakpoint
} from 'components/pcc-components';
import {
  CatCard,
  CatHeading,
  CatTooltip,
  CatTextPassage,
  CatIconSuccessFilled
} from 'components/blocks-components';
import { BlocksCloseEvent } from '@blocks-web-components/events';
import { Image } from 'components/image';
import { MyEquipmentActionMenu } from 'components/my-equipment/my-equipment-action-menu';
import { ServiceInsightIcon } from 'components/my-equipment/service-insight-icon/ServiceInsightIcon';
import { useMyEquipmentContext } from 'components/my-equipment/my-equipment-search-pagination-context';
import { actionTypes } from 'components/my-equipment/types';
import { Equipment, ServiceInsightDetailProps } from 'services/equipment/types';
import { MY_EQP_SCHEMA } from 'components/my-equipment/constants';
import { PAGE_BREAKPOINTS } from 'constants/common';
import styles from './single-equipment-card.module.scss';
import { getRFSEnabledUser } from 'components/request-equipment-service/utils';

export type SingleEquipmentCardProps = {
  equipment: Equipment;
  isDetailsCard?: boolean;
  userDetail: {
    isLoggedIn?: boolean;
    registerUrl?: string;
    isCspUser?: boolean;
    ccidProd?: string;
    isBuyOnBehalf?: boolean;
    isDealerUser?: boolean;
    isInstantAccessUser?: boolean;
  };
  isRequestServiceFlag: boolean;
  startTransition?: TransitionStartFunction;
  serviceInsightsDetails: ServiceInsightDetailProps;
  isAssetLandingFeatureFlag?: boolean;
};

const SingleEquipmentCard = ({
  equipment,
  isDetailsCard = false,
  userDetail,
  isRequestServiceFlag,
  startTransition,
  serviceInsightsDetails,
  isAssetLandingFeatureFlag
}: Readonly<SingleEquipmentCardProps>) => {
  const t = useTranslations();
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const { selectedEquipmentIndex } = useMyEquipmentContext();
  const {
    imageURL,
    serialNumber,
    productFamily,
    model,
    assetName = '',
    seoUrl = '',
    name
  } = equipment;

  const isRequestService = getRFSEnabledUser(userDetail);

  const { imgWidth, imgHeight } = getDesktopImageWidthHeight(isDesktop);
  const isSelectedEquipment = selectedEquipmentIndex > -1;

  const familyName = getFamilyName(model, name ?? productFamily);

  const equipmentHeader = `${model} ${getProductName(familyName)}`;

  const handleRequestService = () => {
    const queryParam = new URLSearchParams({
      isRequestServiceModal: 'true'
    });
    navigateTo(`${seoUrl}?${queryParam.toString()}`);
  };
  const requestService = !isRequestServiceFlag && {
    onClick: () => {
      handleRequestService();
    }
  };

  const kebabActions: Array<actionTypes> = [
    {
      id: MY_EQP_SCHEMA.VIEW_DASHBOARD
    },
    {
      id: MY_EQP_SCHEMA.SHOP_PARTS
    },
    {
      id: MY_EQP_SCHEMA.CAT_PARTS_DIAGRAM
    },
    {
      id: MY_EQP_SCHEMA.REQUEST_SERVICE,
      isActionVisible: isDesktop && isRequestService,
      ...requestService //This should be temporary removed after request Service Modal available in Headless
    },
    {
      id: MY_EQP_SCHEMA.ALP_MAINTENANCE_REPAIRS,
      isActionVisible: isDetailsCard
    },
    {
      id: MY_EQP_SCHEMA.MORE,
      moreOptions: [
        {
          id: MY_EQP_SCHEMA.REQUEST_SERVICE,
          isActionVisible: !isDesktop && isRequestService,
          ...requestService //This should be temporary removed after request Service Modal available in Headless
        },
        {
          id: MY_EQP_SCHEMA.ALP_MAINTENANCE_REPAIRS,
          isActionVisible: !isDetailsCard,
          classNames: cx({
            [styles['contextualmenu-wrapper__list-item']]: !isDetailsCard
          })
        },
        {
          id: MY_EQP_SCHEMA.CLEAR_SELECTION,
          isActionVisible: isSelectedEquipment
        },
        {
          id: MY_EQP_SCHEMA.PLEASE_SELECT,
          isActionVisible: !isSelectedEquipment
        },
        {
          id: MY_EQP_SCHEMA.EDIT,
          isActionVisible: true
        },
        {
          id: MY_EQP_SCHEMA.DELETE,
          isActionVisible: !equipment.isValidatedAsset
        }
      ]
    }
  ];

  const handleClose = (e: BlocksCloseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const modalVariant = useMemo(() => {
    if (isDesktop) {
      return assetName ? 'body-sm' : 'title';
    }
    return assetName ? 'footnote' : 'display-sm';
  }, [isDesktop, assetName]);

  const cardContentStyle = useMemo(() => {
    if (isDetailsCard) {
      return 'm-lg-3 p-md-0';
    } else if (!isDetailsCard && isDesktop) {
      return 'mb-1 m-2 p-1';
    }
  }, [isDetailsCard, isDesktop]);

  return (
    <div
      className={` m-0 ${isDetailsCard ? 'p-0 col-lg-12 px-0' : 'col-lg-6'} ${!isDetailsCard && isDesktop && 'p-0 ps-3'} col-12`}
    >
      <CatCard data-testid="single-equipment-card" removePadding>
        <div
          className={`${styles['single-equipment__header']} ${isDetailsCard ? 'p-3' : ''}`}
          slot="header"
        >
          <CatHeading variant="display-sm">
            {isSelectedEquipment
              ? t('MEQ_CURRENTLY_SHOPPING_FOR')
              : t('SELECT_EQUIPMENT')}
          </CatHeading>
        </div>
        <div
          className={`d-flex position-relative justify-content-between m-2 mt-md-4 ml-md-3 mb-lg-0 p-1 ${cardContentStyle}`}
        >
          <div
            className={`d-flex flex-column flex-md-row flex-lg-column gap-md-4`}
          >
            <Image
              src={imageURL}
              alt={productFamily}
              width={imgWidth}
              height={imgHeight}
              loading="lazy"
              className={`col-12 col-md-4 mb-2 mr-3  d-lg-none ${styles['single-equipment__img']} `}
            />
            <div className="col-md-8 col-12 col-lg-12 mb-2">
              {assetName && (
                <CatHeading variant={isDesktop ? 'title' : 'display-sm'}>
                  <span
                    className={`text-truncate d-block ${styles['single-equipment__heading']}`}
                  >
                    {assetName}
                  </span>
                </CatHeading>
              )}
              {equipmentHeader && (
                <CatHeading variant={modalVariant}>
                  <span
                    className={`text-truncate d-block ${styles['single-equipment__heading']}`}
                  >
                    {equipmentHeader}
                  </span>
                </CatHeading>
              )}
              {serialNumber && (
                <CatHeading variant={isDesktop ? 'body-sm' : 'footnote'}>
                  <span
                    className={`text-truncate d-block ${styles['single-equipment__heading']}`}
                  >
                    {serialNumber}
                  </span>
                </CatHeading>
              )}
              <Image
                src={imageURL}
                alt={productFamily}
                loading="lazy"
                width={imgWidth}
                height={imgHeight}
                className={`d-none d-flex m-auto  d-lg-block ${styles['single-equipment__img']}`}
              />
              <Conditional test={isDesktop}>
                <ServiceInsightIcon
                  equipmentDetails={equipment}
                  serviceInsightsDetails={serviceInsightsDetails}
                  isCspUser={userDetail?.isCspUser}
                  isDisplayLink
                />
              </Conditional>
              <div className="d-block d-sm-block d-md-block pb-lg-0 ">
                <Conditional test={Object.keys(equipment).length > 0}>
                  <div
                    className={
                      isDetailsCard ? styles['equipment-actions'] : 'py-3'
                    }
                  >
                    <CustomerAccountEquipmentActionWrapper
                      classname={styles['equipment-actions__container']}
                    >
                      <MyEquipmentActionMenu
                        actionItems={kebabActions}
                        equipmentDetails={equipment}
                        userDetail={userDetail}
                        startTransition={startTransition}
                        isDetailsCard={isDetailsCard}
                        selectedEquipmentIndex={selectedEquipmentIndex}
                        isAssetLandingFeatureFlag={isAssetLandingFeatureFlag}
                      />
                    </CustomerAccountEquipmentActionWrapper>
                  </div>
                </Conditional>
              </div>
            </div>
          </div>
          <div className={`d-flex ${styles['single-equipment__icon_wrapper']}`}>
            <Conditional test={!isDesktop && !isDetailsCard}>
              <ServiceInsightIcon
                equipmentDetails={equipment}
                highlightSelectedEquipment={isSelectedEquipment}
                serviceInsightsDetails={serviceInsightsDetails}
                isCspUser={userDetail?.isCspUser}
              />
            </Conditional>
            {isSelectedEquipment && !isDetailsCard && (
              <CatTooltip
                onBlTooltipClose={handleClose}
                position="auto"
                isInteractive={!isDesktop}
              >
                <CatIconSuccessFilled
                  slot="trigger"
                  size={isDesktop ? 'lg' : null}
                  data-testid="selected-equipment-icon"
                  tabIndex={0}
                  aria-label={t('SELECTED_EQUIPMENT')}
                />
                <CatTextPassage size="xs">
                  {t('SELECTED_EQUIPMENT')}
                </CatTextPassage>
              </CatTooltip>
            )}
          </div>
        </div>
      </CatCard>
    </div>
  );
};

export default SingleEquipmentCard;
